import { BE1_API } from '../config/api';
import { errorFromResponse } from '../services/errorText';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// "데이터 정의" = SAM파일(구분자 파일)/DB테이블을 미리 등록해두고, 디자이너에서 재사용하는 레이아웃 정의.
// 서버(backend/app/services/data_definitions_store.py)에 JSON 파일로 저장되며,
// path 는 'sam' 또는 'db' 로 시작하는 그룹 폴더 경로 (예: 'sam/매출그룹/2026').
export type DataDefinitionType = 'sam' | 'db';

export interface DataDefinitionColumn {
  name: string;
  dataType: string;
}

export interface DataDefinitionSource {
  // SAM파일 — format: delimited(가변폭)/fixed(고정폭), 고정폭은 fieldWidths(바이트)로 자른다
  filepath?: string;
  hasHeader?: boolean;
  delimiter?: string;
  format?: 'delimited' | 'fixed';
  encoding?: string;
  fieldWidths?: string;
  fieldNames?: string;
  // DB테이블 — 커넥션은 접속 계정이라 여러 스키마가 보인다. 스키마를 함께 지정한다.
  connection?: string;
  schema?: string;
  table?: string;
}

export interface FormatProfile {
  id: string; name: string; format: 'delimited' | 'fixed';
  delimiter: string; hasHeader: boolean; encoding: string; description?: string;
}

export interface SamType {
  id: string; name: string; profileId: string;
  columns: DataDefinitionColumn[]; fieldWidths?: string; pathPattern?: string; description?: string;
}

export interface DataDefinition {
  id: string;
  type: DataDefinitionType;
  name: string;
  path: string;
  source: DataDefinitionSource;
  columns: DataDefinitionColumn[];
  updatedAt: string;
}


export const fetchSamTypes = createAsyncThunk('dataDefinitions/fetchSamTypes', async () => {
  const res = await fetch(`${BE1_API}/api/sam-types/`);
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()) as { profiles: FormatProfile[]; types: SamType[] };
});

interface DataDefinitionsState {
  folders: string[];
  items: DataDefinition[];
  samProfiles: FormatProfile[];
  samTypes: SamType[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error?: string;
}

const initialState: DataDefinitionsState = {
  folders: [],
  items: [],
  samProfiles: [],
  samTypes: [],
  status: 'idle',
};

export const fetchDataDefinitions = createAsyncThunk('dataDefinitions/fetchAll', async () => {
  const res = await fetch(`${BE1_API}/api/data-definitions/`);
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()) as { folders: string[]; items: DataDefinition[] };
});

export const createFolder = createAsyncThunk(
  'dataDefinitions/createFolder',
  async (path: string, { dispatch }) => {
    const res = await fetch(`${BE1_API}/api/data-definitions/folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path }),
    });
    if (!res.ok) throw new Error(await res.text());
    await dispatch(fetchDataDefinitions());
  }
);

export const createDefinition = createAsyncThunk(
  'dataDefinitions/createItem',
  async (payload: { path: string; name: string; source: DataDefinitionSource }, { dispatch }) => {
    const res = await fetch(`${BE1_API}/api/data-definitions/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await res.text());
    const item = (await res.json()) as DataDefinition;
    await dispatch(fetchDataDefinitions());
    return item;
  }
);

/** 데이터 정의 수정 — 서버가 이름·소스를 바꾸고 레이아웃을 다시 읽는다. id·폴더는 그대로다 */
export const updateDefinition = createAsyncThunk(
  'dataDefinitions/update',
  async (payload: { id: string; name: string; source: DataDefinitionSource }, { dispatch }) => {
    const res = await fetch(`${BE1_API}/api/data-definitions/items/${encodeURIComponent(payload.id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: payload.name, source: payload.source }),
    });
    if (!res.ok) throw await errorFromResponse(res, res.statusText);
    const item = (await res.json()) as DataDefinition;
    await dispatch(fetchDataDefinitions());
    return item;
  }
);

export const refreshDefinition = createAsyncThunk(
  'dataDefinitions/refresh',
  async (id: string, { dispatch }) => {
    const res = await fetch(`${BE1_API}/api/data-definitions/items/${id}/refresh`, { method: 'PUT' });
    if (!res.ok) throw new Error(await res.text());
    await dispatch(fetchDataDefinitions());
  }
);

export const moveDefinition = createAsyncThunk(
  'dataDefinitions/move',
  async (payload: { id: string; newPath: string }, { dispatch }) => {
    const res = await fetch(`${BE1_API}/api/data-definitions/items/${payload.id}/move`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newPath: payload.newPath }),
    });
    if (!res.ok) throw new Error(await res.text());
    await dispatch(fetchDataDefinitions());
  }
);

export const copyDefinition = createAsyncThunk(
  'dataDefinitions/copy',
  async (payload: { id: string; newPath: string }, { dispatch }) => {
    const res = await fetch(`${BE1_API}/api/data-definitions/items/${payload.id}/copy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newPath: payload.newPath }),
    });
    if (!res.ok) throw new Error(await res.text());
    await dispatch(fetchDataDefinitions());
  }
);

/**
 * 데이터 정의 삭제 — 서버는 바로 지우지 않고 삭제 보관함으로 옮긴다(되살릴 수 있다).
 * 실패하면 서버가 보낸 오류 코드를 화면 언어로 바꿔 던진다(부르는 쪽이 알림으로 보인다).
 */
export const deleteDefinition = createAsyncThunk(
  'dataDefinitions/delete',
  async (id: string, { dispatch }) => {
    const res = await fetch(`${BE1_API}/api/data-definitions/items/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!res.ok) throw await errorFromResponse(res, res.statusText);
    await dispatch(fetchDataDefinitions());
  }
);

/** 빈 그룹 폴더 삭제 — 안에 정의가 남아 있으면 서버가 거절한다(def.folderNotEmpty) */
export const deleteFolder = createAsyncThunk(
  'dataDefinitions/deleteFolder',
  async (path: string, { dispatch }) => {
    const res = await fetch(`${BE1_API}/api/data-definitions/folders?path=${encodeURIComponent(path)}`, { method: 'DELETE' });
    if (!res.ok) throw await errorFromResponse(res, res.statusText);
    await dispatch(fetchDataDefinitions());
  }
);

const dataDefinitionsSlice = createSlice({
  name: 'dataDefinitions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchSamTypes.fulfilled, (state, action) => {
      state.samProfiles = action.payload.profiles || [];
      state.samTypes = action.payload.types || [];
    });
    builder
      .addCase(fetchDataDefinitions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDataDefinitions.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.folders = action.payload.folders;
        state.items = action.payload.items;
      })
      .addCase(fetchDataDefinitions.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

export default dataDefinitionsSlice.reducer;
